import React from "react";
import { useLocalStorage } from "react-use";
import { type FileItem, type FolderItem } from "@/types/fse";
import { Artefact, CitationInfo } from "@/modules/chat/chat-ui.types";
import { CurrentPdfContext, type RightDrawerContentType } from "@/contexts/setPdfContext.consts";
import type { SidePanelTab } from "@/components/chat/types";

export const CurrentPdfProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [activeTabStorage, setActiveTabStorage] = useLocalStorage<SidePanelTab | null>("chat-right-sidebar-tab", "pdf");
  const activeTab = activeTabStorage === undefined ? "pdf" : activeTabStorage;
  const setActiveTab = React.useCallback(
    (tab: SidePanelTab | null) => {
      setActiveTabStorage(tab);
    },
    [setActiveTabStorage],
  );
  const [rootItem, setRootItem] = React.useState<FolderItem | FileItem>();
  const [currentPdfId, setCurrentPdfId] = React.useState<string | null>(null);
  const [isRightDrawerVisible, setIsRightDrawerVisible] = React.useState<boolean>(false);
  const [rightDrawerContent, setRightDrawerContent] = React.useState<RightDrawerContentType>("pdf");
  const [currentCitation, setCurrentCitation] = React.useState<CitationInfo | null>(null);
  const [selectedFiles, setSelectedFiles] = React.useState<string[]>([]);
  const [artefacts, setArtefacts] = React.useState<Artefact[]>([]);
  const [selectedArtefactId, setSelectedArtefactId] = React.useState<string | null>(null);

  const handleFileSelect = React.useCallback((file: FileItem, isSelected: boolean) => {
    setSelectedFiles(prev => {
      if (!prev.includes(file.id) && isSelected) {
        return [...prev, file.id];
      } else {
        return prev.filter(id => id !== file.id);
      }
    });
  }, []);

  const handleCitationClick = React.useCallback(
    (citation: CitationInfo) => {
      // Only set PDF ID if it's different to avoid unnecessary reload
      if (currentPdfId !== citation.fseId) {
        setCurrentPdfId(citation.fseId);
      }
      setRightDrawerContent("pdf");
      setActiveTab("pdf");
      setIsRightDrawerVisible(true);
      setCurrentCitation(citation);
    },
    [currentPdfId, setActiveTab],
  );

  const handleSelectedArtefactId = (artefactId: string | null) => {
    if (artefactId) {
      setRightDrawerContent("artefacts");
      setActiveTab("artefacts");
    }
    setSelectedArtefactId(artefactId);
  };

  return (
    <CurrentPdfContext.Provider
      value={{
        activeTab,
        setActiveTab,
        rootItem,
        setRootItem,
        selectedFiles,
        handleFileSelect,
        setSelectedFiles,
        currentPdfId,
        setCurrentPdfId,
        isRightDrawerVisible,
        setIsRightDrawerVisible,
        rightDrawerContent,
        setRightDrawerContent,
        handleCitationClick,
        currentCitation,
        setSelectedArtefactId: handleSelectedArtefactId,
        artefacts: artefacts,
        selectedArtefactId,
        setArtefacts,
        setCurrentCitation,
      }}
    >
      {children}
    </CurrentPdfContext.Provider>
  );
};
